import type { NodeGraph } from '../data-model/types';
import type { AudioSetup } from '../data-model/audioSetupTypes';
import type { CompilationResult } from '../compile-contract';
import type { ShaderCompiler } from '../runtime/types';
import type { FrameAudioState } from './OfflineAudioProvider';
import type { ExportRenderPathConfig, ExportRenderPathResult } from './ExportRenderPath';
import {
  createBlurGaussianSeparableV1Runtime,
  destroyBlurGaussianSeparableV1Runtime,
  validateBlurGaussianSeparableV1ShaderModules,
} from '../runtime/renderBackends/blurGaussianSeparablePassPlanRuntime';
import {
  createGlowBloomV1Runtime,
  destroyGlowBloomV1Runtime,
  validateGlowBloomV1ShaderModules,
} from '../runtime/renderBackends/glowBloomPassPlanRuntime';
import {
  createBokehV1Runtime,
  destroyBokehV1Runtime,
  validateBokehV1ShaderModules,
} from '../runtime/renderBackends/bokehPassPlanRuntime';
import {
  createCrepuscularRaysV1Runtime,
  destroyCrepuscularRaysV1Runtime,
  validateCrepuscularRaysV1ShaderModules,
} from '../runtime/renderBackends/crepuscularRaysPassPlanRuntime';
import {
  applyParamSlotUpdates,
  encodeWebGpuPassPlanFrame,
  fillPassPlanParamsBuffer,
  packPassPlanParamsFromGraph,
} from '../runtime/renderBackends/webgpuPassPlanExecutor';
import { selectWebGpuPresentationFormat } from '../runtime/renderBackends/webgpuPresentationFormat';
import { refreshExportArrangementBakeCaches } from './refreshExportArrangementBakeCaches';

type NavigatorGpu = { gpu?: GPU };

function getNavigatorGpu(): GPU | undefined {
  const nav = typeof navigator !== 'undefined' ? (navigator as unknown as NavigatorGpu) : undefined;
  return nav?.gpu;
}

/** Chromium warns (and ignores) `powerPreference` on Windows; see https://crbug.com/369219127 */
function requestAdapterOptions(): GPURequestAdapterOptions {
  if (typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent)) {
    return {};
  }
  return { powerPreference: 'high-performance' };
}

async function waitForCanvasPresentation(): Promise<void> {
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

export type WebGpuExportRenderPathBuildResult =
  | { ok: true; path: ExportRenderPathResult }
  | { ok: false; reason: string; compilation?: CompilationResult; error?: Error };

/**
 * Create a WebGPU-based export render path for video export.
 *
 * The returned path renders into a dedicated canvas (not the preview canvas) and synchronizes
 * GPU submission completion before handing frames to the encoder to avoid blank/black frames.
 */
export async function createWebGpuVideoExportRenderPath(
  graph: NodeGraph,
  compiler: ShaderCompiler,
  audioSetup: AudioSetup,
  config: ExportRenderPathConfig
): Promise<WebGpuExportRenderPathBuildResult> {
  const gpu = getNavigatorGpu();
  if (!gpu) return { ok: false, reason: 'navigator.gpu.absent' };

  let adapter: GPUAdapter | null;
  try {
    adapter = await gpu.requestAdapter(requestAdapterOptions());
  } catch (e) {
    const err = e instanceof Error ? e : new Error('WebGPU requestAdapter failed', { cause: e });
    return { ok: false, reason: 'requestAdapter.failed', error: err };
  }
  if (!adapter) return { ok: false, reason: 'requestAdapter.null' };

  let device: GPUDevice;
  try {
    device = await adapter.requestDevice();
  } catch (e) {
    const err = e instanceof Error ? e : new Error('WebGPU requestDevice failed', { cause: e });
    return { ok: false, reason: 'requestDevice.failed', error: err };
  }

  const compilation = compiler.compile(graph, audioSetup, { backend: 'webgpu' });
  if (compilation.metadata.errors.length > 0) {
    return { ok: false, reason: 'compile.errors', compilation };
  }
  if (compilation.backend !== 'webgpu') {
    return { ok: false, reason: `compile.backend.${compilation.backend}`, compilation };
  }
  if (!compilation.supported) {
    return { ok: false, reason: 'compile.unsupported', compilation };
  }

  refreshExportArrangementBakeCaches(graph, audioSetup);

  const width = Math.max(1, Math.floor(config.width));
  const height = Math.max(1, Math.floor(config.height));
  const frameRate = config.frameRate;
  const startTimeSeconds = config.startTimeSeconds ?? 0;

  const canvas = Object.assign(document.createElement('canvas'), { width, height }) as HTMLCanvasElement;
  const ctx0 = canvas.getContext('webgpu') as GPUCanvasContext | null;
  if (!ctx0) return { ok: false, reason: 'canvasContext.webgpu.null', compilation };
  const ctx: GPUCanvasContext = ctx0;

  const presentationFormat = selectWebGpuPresentationFormat(gpu);
  // Explicitly set size so the canvas backing store matches export resolution.
  // This avoids driver/browser quirks where the WebGPU swapchain size can lag behind element dimensions.
  ctx.configure({
    device,
    format: presentationFormat,
    alphaMode: 'premultiplied',
    // Some TS WebGPU lib versions don't include `size` in `GPUCanvasConfiguration` yet.
    // Runtime supports it; keep the behavior and cast locally.
    size: { width, height },
  } as unknown as GPUCanvasConfiguration);

  if (compilation.webgpuPassPlan) {
    const plan = compilation.webgpuPassPlan;
    if (plan.kind === 'pass.blur.gaussian-separable.v1') {
      const wgslErr = await validateBlurGaussianSeparableV1ShaderModules(device, plan);
      if (wgslErr) {
        return { ok: false, reason: 'wgsl.compile', compilation, error: new Error(wgslErr) };
      }

      device.pushErrorScope('validation');
      const rt = createBlurGaussianSeparableV1Runtime(
        device,
        plan,
        compilation.paramLayout,
        presentationFormat
      );
      const validationErr = await device.popErrorScope();
      if (validationErr) {
        destroyBlurGaussianSeparableV1Runtime(rt);
        return {
          ok: false,
          reason: 'webgpu.validation',
          compilation,
          error: new Error(validationErr.message ?? String(validationErr)),
        };
      }

      const baseParamsData = new Float32Array(rt.paramsData.length);
      fillPassPlanParamsBuffer(graph, compilation.paramLayout, baseParamsData, {
        uniforms: compilation.uniforms,
      });

      let blurDisposed = false;

      async function renderFrameBlurPassPlan(
        frameIndex: number,
        frameState: FrameAudioState
      ): Promise<HTMLCanvasElement> {
        if (blurDisposed) throw new Error('WebGpuVideoExportRenderPath already disposed');

        const timeSeconds = startTimeSeconds + frameIndex / frameRate;
        rt.time = timeSeconds;
        rt.timelineTime = frameState.timelineTime;
        rt.paramsData.set(baseParamsData);
        applyParamSlotUpdates(rt.paramsData, compilation.paramLayout, frameState.uniformUpdates);
        rt.paramsDirty = true;

        encodeWebGpuPassPlanFrame(
          {
            device,
            queue: device.queue,
            width,
            height,
            presentTargetView: ctx.getCurrentTexture().createView(),
          },
          rt
        );

        await device.queue.onSubmittedWorkDone();
        await waitForCanvasPresentation();

        return canvas;
      }

      function disposeBlurPassPlan(): void {
        if (blurDisposed) return;
        blurDisposed = true;
        destroyBlurGaussianSeparableV1Runtime(rt);
      }

      const path: ExportRenderPathResult = {
        renderFrame(frameIndex: number): HTMLCanvasElement {
          throw new Error(
            `WebGpuVideoExportRenderPath requires async render; use renderFrameAsync (frameIndex=${frameIndex}).`
          );
        },
        renderFrameAsync: renderFrameBlurPassPlan,
        getCanvas(): HTMLCanvasElement {
          return canvas;
        },
        dispose: disposeBlurPassPlan,
      };

      return { ok: true, path };
    }

    if (plan.kind === 'pass.crepuscular-rays.v1') {
      const wgslErr = await validateCrepuscularRaysV1ShaderModules(device, plan);
      if (wgslErr) {
        return { ok: false, reason: 'wgsl.compile', compilation, error: new Error(wgslErr) };
      }

      device.pushErrorScope('validation');
      const rt = createCrepuscularRaysV1Runtime(
        device,
        plan,
        compilation.paramLayout,
        presentationFormat
      );
      const validationErr = await device.popErrorScope();
      if (validationErr) {
        destroyCrepuscularRaysV1Runtime(rt);
        return {
          ok: false,
          reason: 'webgpu.validation',
          compilation,
          error: new Error(validationErr.message ?? String(validationErr)),
        };
      }

      const baseParamsData = new Float32Array(rt.paramsData.length);
      fillPassPlanParamsBuffer(graph, compilation.paramLayout, baseParamsData, {
        uniforms: compilation.uniforms,
      });

      let crepuscularDisposed = false;

      async function renderFrameCrepuscularPassPlan(
        frameIndex: number,
        frameState: FrameAudioState
      ): Promise<HTMLCanvasElement> {
        if (crepuscularDisposed) throw new Error('WebGpuVideoExportRenderPath already disposed');

        const timeSeconds = startTimeSeconds + frameIndex / frameRate;
        rt.time = timeSeconds;
        rt.timelineTime = frameState.timelineTime;
        rt.paramsData.set(baseParamsData);
        applyParamSlotUpdates(rt.paramsData, compilation.paramLayout, frameState.uniformUpdates);
        rt.paramsDirty = true;

        encodeWebGpuPassPlanFrame(
          {
            device,
            queue: device.queue,
            width,
            height,
            presentTargetView: ctx.getCurrentTexture().createView(),
          },
          rt
        );

        await device.queue.onSubmittedWorkDone();
        await waitForCanvasPresentation();

        return canvas;
      }

      function disposeCrepuscularPassPlan(): void {
        if (crepuscularDisposed) return;
        crepuscularDisposed = true;
        destroyCrepuscularRaysV1Runtime(rt);
      }

      const path: ExportRenderPathResult = {
        renderFrame(frameIndex: number): HTMLCanvasElement {
          throw new Error(
            `WebGpuVideoExportRenderPath requires async render; use renderFrameAsync (frameIndex=${frameIndex}).`
          );
        },
        renderFrameAsync: renderFrameCrepuscularPassPlan,
        getCanvas(): HTMLCanvasElement {
          return canvas;
        },
        dispose: disposeCrepuscularPassPlan,
      };

      return { ok: true, path };
    }

    if (plan.kind === 'pass.glow-bloom.v1') {
      const wgslErr = await validateGlowBloomV1ShaderModules(device, plan);
      if (wgslErr) {
        return { ok: false, reason: 'wgsl.compile', compilation, error: new Error(wgslErr) };
      }

      device.pushErrorScope('validation');
      const rt = createGlowBloomV1Runtime(device, plan, compilation.paramLayout, presentationFormat);
      const validationErr = await device.popErrorScope();
      if (validationErr) {
        destroyGlowBloomV1Runtime(rt);
        return {
          ok: false,
          reason: 'webgpu.validation',
          compilation,
          error: new Error(validationErr.message ?? String(validationErr)),
        };
      }

      const baseParamsData = new Float32Array(rt.paramsData.length);
      fillPassPlanParamsBuffer(graph, compilation.paramLayout, baseParamsData, {
        uniforms: compilation.uniforms,
      });

      let glowBloomDisposed = false;

      async function renderFrameGlowBloomPassPlan(
        frameIndex: number,
        frameState: FrameAudioState
      ): Promise<HTMLCanvasElement> {
        if (glowBloomDisposed) throw new Error('WebGpuVideoExportRenderPath already disposed');

        const timeSeconds = startTimeSeconds + frameIndex / frameRate;
        rt.time = timeSeconds;
        rt.timelineTime = frameState.timelineTime;
        rt.paramsData.set(baseParamsData);
        applyParamSlotUpdates(rt.paramsData, compilation.paramLayout, frameState.uniformUpdates);
        rt.paramsDirty = true;

        encodeWebGpuPassPlanFrame(
          {
            device,
            queue: device.queue,
            width,
            height,
            presentTargetView: ctx.getCurrentTexture().createView(),
          },
          rt
        );

        await device.queue.onSubmittedWorkDone();
        await waitForCanvasPresentation();

        return canvas;
      }

      function disposeGlowBloomPassPlan(): void {
        if (glowBloomDisposed) return;
        glowBloomDisposed = true;
        destroyGlowBloomV1Runtime(rt);
      }

      const path: ExportRenderPathResult = {
        renderFrame(frameIndex: number): HTMLCanvasElement {
          throw new Error(
            `WebGpuVideoExportRenderPath requires async render; use renderFrameAsync (frameIndex=${frameIndex}).`
          );
        },
        renderFrameAsync: renderFrameGlowBloomPassPlan,
        getCanvas(): HTMLCanvasElement {
          return canvas;
        },
        dispose: disposeGlowBloomPassPlan,
      };

      return { ok: true, path };
    }

    if (plan.kind === 'pass.bokeh.v1') {
      const wgslErr = await validateBokehV1ShaderModules(device, plan);
      if (wgslErr) {
        return { ok: false, reason: 'wgsl.compile', compilation, error: new Error(wgslErr) };
      }

      device.pushErrorScope('validation');
      const rt = createBokehV1Runtime(device, plan, compilation.paramLayout, presentationFormat);
      const validationErr = await device.popErrorScope();
      if (validationErr) {
        destroyBokehV1Runtime(rt);
        return {
          ok: false,
          reason: 'webgpu.validation',
          compilation,
          error: new Error(validationErr.message ?? String(validationErr)),
        };
      }

      const baseParamsData = new Float32Array(rt.paramsData.length);
      fillPassPlanParamsBuffer(graph, compilation.paramLayout, baseParamsData, {
        uniforms: compilation.uniforms,
      });

      let bokehDisposed = false;

      async function renderFrameBokehPassPlan(frameIndex: number, frameState: FrameAudioState): Promise<HTMLCanvasElement> {
        if (bokehDisposed) throw new Error('WebGpuVideoExportRenderPath already disposed');

        const timeSeconds = startTimeSeconds + frameIndex / frameRate;
        rt.time = timeSeconds;
        rt.timelineTime = frameState.timelineTime;
        rt.paramsData.set(baseParamsData);
        applyParamSlotUpdates(rt.paramsData, compilation.paramLayout, frameState.uniformUpdates);
        rt.paramsDirty = true;

        encodeWebGpuPassPlanFrame(
          {
            device,
            queue: device.queue,
            width,
            height,
            presentTargetView: ctx.getCurrentTexture().createView(),
          },
          rt
        );

        await device.queue.onSubmittedWorkDone();
        await waitForCanvasPresentation();
        return canvas;
      }

      function disposeBokehPassPlan(): void {
        if (bokehDisposed) return;
        bokehDisposed = true;
        destroyBokehV1Runtime(rt);
      }

      const path: ExportRenderPathResult = {
        renderFrame(frameIndex: number): HTMLCanvasElement {
          throw new Error(
            `WebGpuVideoExportRenderPath requires async render; use renderFrameAsync (frameIndex=${frameIndex}).`
          );
        },
        renderFrameAsync: renderFrameBokehPassPlan,
        getCanvas(): HTMLCanvasElement {
          return canvas;
        },
        dispose: disposeBokehPassPlan,
      };

      return { ok: true, path };
    }

    const fallbackKind = (plan as unknown as { kind: string }).kind;
    return {
      ok: false,
      reason: `compile.passplan.unsupported.${fallbackKind}`,
      compilation,
    };
  }

  // Surface validation issues (invalid WGSL, layout mismatches, etc.) as explicit errors.
  device.pushErrorScope('validation');
  const module = device.createShaderModule({ code: compilation.code });
  if (typeof module.getCompilationInfo === 'function') {
    const info = await module.getCompilationInfo();
    const errs = info.messages.filter((m) => m.type === 'error');
    if (errs.length > 0) {
      device.popErrorScope().catch(() => null);
      const msg = errs
        .slice(0, 8)
        .map((m) => `[wgsl:${m.lineNum}:${m.linePos}] ${m.message}`.trim())
        .join('; ');
      return { ok: false, reason: 'wgsl.compile', compilation, error: new Error(msg) };
    }
  }

  const globalsBuffer = device.createBuffer({
    size: 8 * 4,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const baseParamsData = packPassPlanParamsFromGraph(graph, compilation.paramLayout, {
    uniforms: compilation.uniforms,
  });
  const paramsData = new Float32Array(baseParamsData);
  const paramsBuffer = device.createBuffer({
    size: paramsData.byteLength,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });

  const bindGroupLayout = device.createBindGroupLayout({
    entries: [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
    ],
  });

  const pipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] });
  const pipeline = device.createRenderPipeline({
    layout: pipelineLayout,
    vertex: { module, entryPoint: 'vs' },
    fragment: { module, entryPoint: 'fs', targets: [{ format: presentationFormat }] },
    primitive: { topology: 'triangle-list' },
  });
  const validationErr = await device.popErrorScope();
  if (validationErr) {
    return {
      ok: false,
      reason: 'webgpu.validation',
      compilation,
      error: new Error(validationErr.message ?? String(validationErr))
    };
  }

  const bindGroup = device.createBindGroup({
    layout: bindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: globalsBuffer } },
      { binding: 1, resource: { buffer: paramsBuffer } },
    ],
  });

  const globalsData = new Float32Array(8);
  let disposed = false;

  async function renderFrame(frameIndex: number, frameState: FrameAudioState): Promise<HTMLCanvasElement> {
    if (disposed) throw new Error('WebGpuVideoExportRenderPath already disposed');

    const timeSeconds = startTimeSeconds + frameIndex / frameRate;
    globalsData[0] = timeSeconds;
    globalsData[1] = frameState.timelineTime;
    globalsData[2] = width;
    globalsData[3] = height;
    globalsData[4] = 0;
    globalsData[5] = 0;
    globalsData[6] = 0;
    globalsData[7] = 0;

    paramsData.set(baseParamsData);
    applyParamSlotUpdates(paramsData, compilation.paramLayout, frameState.uniformUpdates);

    device.queue.writeBuffer(
      globalsBuffer,
      0,
      globalsData.buffer as ArrayBuffer,
      globalsData.byteOffset,
      globalsData.byteLength
    );
    device.queue.writeBuffer(
      paramsBuffer,
      0,
      paramsData.buffer as ArrayBuffer,
      paramsData.byteOffset,
      paramsData.byteLength
    );

    const encoder = device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: ctx.getCurrentTexture().createView(),
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: 'clear',
          storeOp: 'store',
        },
      ],
    });
    pass.setViewport(0, 0, width, height, 0, 1);
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.draw(3, 1, 0, 0);
    pass.end();

    device.queue.submit([encoder.finish()]);

    // Ensure all GPU work is complete before returning the canvas to WebCodecs capture.
    // Without this, some browser/driver combos can intermittently capture a blank/black frame.
    await device.queue.onSubmittedWorkDone();
    await waitForCanvasPresentation();

    return canvas;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    globalsBuffer.destroy();
    paramsBuffer.destroy();
  }

  const path: ExportRenderPathResult = {
    renderFrame(frameIndex: number): HTMLCanvasElement {
      throw new Error(`WebGpuVideoExportRenderPath requires async render; use renderFrameAsync (frameIndex=${frameIndex}).`);
    },
    renderFrameAsync: renderFrame,
    getCanvas(): HTMLCanvasElement {
      return canvas;
    },
    dispose,
  };

  return { ok: true, path };
}

